<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  detail: { type: Object, required: true },
});

const { t } = useI18n();
const property = computed(() => props.detail.imovel || {});
const address = computed(
  () =>
    property.value.logradouro ||
    property.value.title ||
    t('NUCLEO_WORK_ORDERS.TABLE.ADDRESS_UNAVAILABLE')
);
const location = computed(() =>
  [property.value.bairro, property.value.cidade, property.value.uf]
    .filter(Boolean)
    .join(' · ')
);
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat('pt-BR').format(new Date(value))
    : t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED');
const progressWidth = computed(() => {
  const percentage = props.detail.progresso?.percentage || 0;
  if (percentage >= 100) return 'w-full';
  if (percentage >= 75) return 'w-3/4';
  if (percentage >= 50) return 'w-1/2';
  if (percentage >= 25) return 'w-1/4';
  return 'w-0';
});
const progressLabel = computed(() =>
  t('NUCLEO_WORK_ORDERS.DETAIL.SUMMARY.PROGRESS_VALUE', {
    completed: props.detail.progresso?.completed || 0,
    total: props.detail.progresso?.total || 0,
  })
);
</script>

<template>
  <section
    class="grid gap-4 rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card lg:grid-cols-[1.4fr_1fr]"
  >
    <div class="space-y-2">
      <p
        class="text-xs font-semibold uppercase tracking-wide text-lais-primary"
      >
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.SUMMARY.PROPERTY') }}
      </p>
      <h2
        class="font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
      >
        {{ address }}
      </h2>
      <p v-if="location" class="text-sm text-lais-text-muted">{{ location }}</p>
      <p class="text-sm text-lais-text-body">
        {{
          t('NUCLEO_WORK_ORDERS.DETAIL.SUMMARY.REQUESTED_BY_VALUE', {
            name:
              detail.solicitante?.name ||
              t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED'),
          })
        }}
      </p>
    </div>
    <dl class="grid grid-cols-2 gap-4 text-sm">
      <div>
        <dt class="text-lais-text-muted">
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.SUMMARY.STORE') }}
        </dt>
        <dd class="mt-1 font-semibold text-lais-text">
          {{ detail.loja?.name || t('NUCLEO_WORK_ORDERS.TABLE.NOT_INFORMED') }}
        </dd>
      </div>
      <div>
        <dt class="text-lais-text-muted">
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.SUMMARY.DUE_AT') }}
        </dt>
        <dd class="mt-1 font-semibold text-lais-text">
          {{ formatDate(detail.data_previsao) }}
        </dd>
      </div>
      <div class="col-span-2">
        <dt class="flex justify-between text-lais-text-muted">
          <span>{{ t('NUCLEO_WORK_ORDERS.DETAIL.SUMMARY.PROGRESS') }}</span>
          <span>{{ progressLabel }}</span>
        </dt>
        <div class="mt-2 h-2 overflow-hidden rounded-full bg-lais-bg-soft">
          <div
            class="h-full rounded-full bg-lais-primary"
            :class="progressWidth"
          />
        </div>
      </div>
    </dl>
  </section>
</template>
